import { lastDayOfMonth, endOfDay, isMatch, isAfter } from 'date-fns';
import t from '@rcm/i18n-client';
import getAppState from 'app-state';

/**
 * Simple validation for a required field
 *
 * @param value the raw value of the input field
 *
 * The return value can be accessed by subscribing to 'meta' in
 * the field renderer function, then with 'meta.error'
 */
export const required = (value) =>
  value && value?.trim().length > 0 ? undefined : 'Required';

export const zip = (value: string) => {
  const newValue = value ? value.trim() : value;
  const testRegex = /^[0-9]{5}([-]?[0-9]{4})?$/;
  return testRegex.test(newValue) ? undefined : 'Needs to be 5 or 9 digits';
};

export const isValidCvv = (cvv: string) => {
  return !(!isNaN(Number(cvv)) && (cvv.length === 3 || cvv.length === 4)) ? 'Invalid CVV' : null;
};

export const isValidPhoneNumber = (input) => {
  if (input === '' || input === undefined) return 'Required';
  if (input) {
    const cleanedInput = input.replace(/[-\s()+.]/g, '');
    const phonePattern = /^\d{4,13}$/;
    return !phonePattern.test(cleanedInput) ? 'Invalid number' : '';
  }
  return '';
};

export const isValidEmail = (value: string) => {
  return (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/).test(value);
};

export const validateShipDate = (value: string) => {
  value = value?.trim();
  if (!value?.length) return 'Required';
  const dateArray = value.split('/');
  const d = new Date();
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();

  if (
    isMatch(value, 'dd/MM/yyyy') && // does the given date match requested format
    dateArray[2].length === 4 && // 4-digit year pls
    isAfter(
      // can't set a shipment for the past
      new Date(
        Number(dateArray[2]),
        Number(dateArray[1]) - 1,
        Number(dateArray[0])
      ),
      new Date(year, month, day)
    )
  ) {
    return '';
  }
  return 'Date must be valid and follow given format';
};

export const validateExpDate = (v) => {
  const { expirationMonth: f, expirationYear: e } = v;
  const expYear = !e ? null : Number(e.length >= 4 ? e : `20${e}`);
  const expMonth = !f ? null : Number(f);

  if (!expYear || !expMonth)
    return {};

  const now = Date.now();
  const exp = endOfDay(
    lastDayOfMonth(new Date(Number(expYear), Number(expMonth) - 1, 30))
  ).getTime();
  const isValid = exp >= now;
  return isValid
    ? {}
    : {
        expirationMonth: t('Invalid Month'),
        expirationYear: t('Invalid Year'),
      };
};

export const validateShipDateUS = (value: string) => {
  value = value?.trim();
  if (!value?.length) return 'Required';
  const dateArray = value.split('/');
  const d = new Date();
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  if (
    isMatch(value, 'MM/dd/yyyy') && // does the given date match requested format
    dateArray[2].length === 4 && // 4-digit year pls
    isAfter(
      // can't set a shipment for the past
      new Date(
        Number(dateArray[2]),
        Number(dateArray[0]) - 1,
        Number(dateArray[1])
      ),
      new Date(year, month, day)
    )
  ) {
    return '';
  }
  return 'Date must be valid and follow given format';
};

/**
 * Uses Luhn algorithm to validate credit card numbers.
 *
 * I found this function online with a very easy to understand
 * explaination of what is happening, and left the comments in
 * the function to minimize confusion.
 * Here is a link to the post: https://learnersbucket.com/examples/javascript/credit-card-validation-in-javascript/
 *
 * @param ccNumber string credit card number from input field
 * @returns boolean
 */
export const validateCardNumber = (ccNumber: string) => {
  const country = getAppState('site.countryIso3', 'USA');
  if (ccNumber === '1111' && country === 'PHL') return ''; //PHL Bank Transfers use this CC# as an override
  if (!ccNumber?.length) return 'Required';
  if (ccNumber.length >= 13 && ccNumber.length <= 19 && ccNumber.includes('*'))
    return '';
  //Check if the number contains only numeric value
  //and is of between 13 to 19 digits
  const regex = new RegExp('^[0-9]{13,19}$');
  if (!regex.test(ccNumber)) {
    return 'Invalid Number';
  }

  return luhnCheck(ccNumber) ? '' : 'Invalid Number';
};

const luhnCheck = (ccNumber) => {
  let checksum = 0; // running checksum total
  let j = 1; // takes value of 1 or 2

  // Process each digit one by one starting from the last
  for (let i = ccNumber.length - 1; i >= 0; i--) {
    let calc = 0;
    // Extract the next digit and multiply by 1 or 2 on alternative digits.
    calc = Number(ccNumber.charAt(i)) * j;

    // If the result is in two digits add 1 to the checksum total
    if (calc > 9) {
      checksum = checksum + 1;
      calc = calc - 10;
    }

    // Add the units element to the checksum total
    checksum = checksum + calc;

    // Switch the value of j
    if (j == 1) {
      j = 2;
    } else {
      j = 1;
    }
  }

  //Check if it is divisible by 10 or not.
  return checksum % 10 == 0;
};
